import MuiCheckbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MuiTypography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { tokens } from "../tokens";
import { cleanProps } from "../internal/cleanProps";
import type { CheckboxProps } from "../types";

/**
 * Composed export: `Checkbox` + `FormControlLabel`, satisfying the surface
 * item "Checkbox with FormControlLabel" as one adapter component — a
 * checkbox can never be rendered from this module without an accessible
 * name. Neither upstream component is generic, so no containment note
 * applies beyond "no Material UI type crosses the boundary".
 *
 * `variant="card"` (default) is the design system's bordered, tinted row;
 * `variant="plain"` is `frp-checkbox-plain.js`'s de-carded row (ADR-20260917-gh2a),
 * for a long coverage list. The card wrapper is this adapter's own `Box`,
 * styled from `tokens.ts` — Material UI's `Checkbox`/`FormControlLabel`
 * internals are not restyled.
 *
 * ADR-20261016-k9wt: `labelVisuallyHidden` is opt-in. `label` always stays
 * the control's accessible name (it is still the text passed to
 * `FormControlLabel`, which sets the native `<label>`/`aria` association) —
 * setting the flag only wraps that same text in a standard clip-based
 * visually-hidden style, so sighted users no longer see it duplicated next
 * to a row that already shows the same title on screen, while assistive
 * technology reads it exactly as before. Only `SectionListRoute`'s
 * select-to-assign rows set it; `ExposureQuestionsEngine` and
 * `DeclarationRoute` are unchanged.
 */
export function Checkbox({
  id,
  label,
  checked,
  onChange,
  variant = "card",
  help,
  error,
  required,
  disabled,
  name,
  testId,
  labelVisuallyHidden,
}: Readonly<CheckboxProps>) {
  const helpId = help !== undefined ? `${id}-help` : undefined;
  const plain = variant !== "card";

  const visibleLabel = labelVisuallyHidden === true ? (
    <Box
      component="span"
      sx={{
        position: "absolute",
        // Explicit top/left (ADR-20261016-k9wt, measured): without them the browser falls back to
        // computing a "static position" for this out-of-flow box from where it would have sat in
        // normal flow, inside a long row of sibling content -- in SectionListRoute's real row
        // layout (never reproduced in an isolated snippet) that pushed the box's containing block
        // far to the right of the viewport, which does not render anything there (the box is still
        // 1x1 and clipped) but does inflate `document.documentElement`'s own scrollable width,
        // which a full-page screenshot then captures as real, blank, extra canvas. Pinning to the
        // containing block's own 0,0 corner removes the "static position" calculation entirely, so
        // this 1x1 box can never extend any ancestor's scrollable area.
        top: 0,
        left: 0,
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
    >
      {label}
    </Box>
  ) : (
    label
  );

  const row = (
    <FormControlLabel
      control={
        <MuiCheckbox
          id={id}
          checked={checked}
          onChange={(_event, next) => {
            onChange(next);
          }}
          {...cleanProps({ required, disabled, name, "aria-describedby": helpId, "data-testid": testId })}
        />
      }
      sx={{ m: 0, alignItems: "flex-start", "& .MuiFormControlLabel-asterisk": { display: "none" } }}
      label={<Box component="span" sx={{ display: "block", pt: `${tokens.spacing.xs}px` }}>{visibleLabel}</Box>}
    />
  );

  // ADR-20261102-h2fd: the H2 design system's Checkbox. `card` is the component's bordered box,
  // filled brand[50] when checked; `plain` is H2's `ds-checkbox` departure -- no box, help text
  // aligned with the label 48px in.
  return (
    <Box
      sx={
        plain
          ? {}
          : {
              mb: `${tokens.spacing.lg}px`,
              border: `${error === true ? border.emphasisWidth : border.controlWidth}px solid ${error === true ? c.status.error.text : c.neutral.borderControl}`,
              borderRadius: `${tokens.interactionState.radius.control}px`,
              backgroundColor: checked ? c.brand[50] : c.neutral.white,
              p: `${tokens.spacing.sm}px`,
              minHeight: tokens.interactionState.targetSize.touch,
            }
      }
    >
      {row}
      {help !== undefined ? (
        <MuiTypography
          id={helpId}
          component="p"
          variant="caption"
          sx={{
            display: "block",
            color: error === true ? c.status.error.text : c.neutral.inkMuted,
            fontWeight: error === true ? tokens.typography.weights.bold : tokens.typography.weights.regular,
            mt: `${tokens.spacing.xxs}px`,
            pl: plain ? "48px" : 0,
          }}
        >
          {help}
        </MuiTypography>
      ) : null}
    </Box>
  );
}

const c = tokens.colour;
const border = tokens.interactionState.border;
